import React, { useEffect, useState } from 'react';
import {
    Shield,
    AlertTriangle,
    RefreshCw,
    Lock,
    Unlock,
    LogIn,
    LogOut,
    Search,
    Plus,
    Edit2,
    Trash2,
    FileText,
    List,
    CheckCircle2,
    XCircle,
    Activity,
    Copy,
    ChevronDown,
    ChevronUp,
    PackageX,
    Container
} from 'lucide-react';
import { LysepAPI, getBaseURL, getToken } from '../../../contexts/ApiContext';
import { toast } from 'react-toastify';
import { useTasks } from '../../../contexts/TaskContext';
import FirewallRuleModal from '../components/FirewallRuleModal';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../../components/ui/Table";
import { Badge } from "../../../components/ui/Badge";
import { IconButton } from "../../../components/ui/Button";

const FirewallTab = ({ serverId }) => {
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [uninstalling, setUninstalling] = useState(false);
    const [config, setConfig] = useState(null);
    const [rules, setRules] = useState([]);
    const [tabValue, setTabValue] = useState(0); // 0: Rules, 1: Preview
    const [rulesPreview, setRulesPreview] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [currentHash, setCurrentHash] = useState('');
    const [isDeployed, setIsDeployed] = useState(false);

    const { addTask, updateTaskLogs, finishTask } = useTasks();

    const fetchData = async (showLoader = true) => {
        if (showLoader) setLoading(true);
        try {
            const resp = await LysepAPI.get(`/api/servers/${serverId}/firewall`);
            if (resp.data.state) {
                const configData = resp.data.data.config;
                setConfig(configData);
                setRules(resp.data.data.rules);
                setCurrentHash(resp.data.data.current_rules_hash);
                // Firewall is deployed if remote_serial exists and is not empty
                setIsDeployed(configData?.remote_serial && configData.remote_serial !== '');
            }
        } catch (err) {
            toast.error('Veriler alınamadı');
        } finally {
            if (showLoader) setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [serverId]);

    const fetchPreview = async () => {
        setPreviewLoading(true);
        try {
            const resp = await LysepAPI.get(`/api/servers/${serverId}/firewall/preview`);
            if (resp.data.state) {
                setRulesPreview(resp.data.data);
            }
        } catch (err) {
            toast.error('Önizleme yüklenemedi');
        } finally {
            setPreviewLoading(false);
        }
    };

    useEffect(() => {
        if (tabValue === 1) {
            fetchPreview();
        }
    }, [tabValue, config, rules]);

    const handleCopyPreview = () => {
        navigator.clipboard.writeText(rulesPreview);
        toast.success('Pano kopyalandı');
    };

    const handleToggleStatus = async () => {
        try {
            const newStatus = !config.status;
            const resp = await LysepAPI.put(`/api/servers/${serverId}/firewall/config/${config.id}`, { status: newStatus });
            if (resp.data.state) {
                setConfig(resp.data.data);
                toast.success(`Güvenlik duvarı ${newStatus ? 'açıldı' : 'kapatıldı'}`);
                fetchData(false);
            }
        } catch (err) {
            toast.error('İşlem başarısız');
        }
    };

    const handleUpdatePolicy = async (field, value) => {
        try {
            const resp = await LysepAPI.put(`/api/servers/${serverId}/firewall/config/${config.id}`, { [field]: value });
            if (resp.data.state) {
                toast.success('Politika güncellendi');
                fetchData(false);
            }
        } catch (err) {
            toast.error('Güncelleme başarısız');
        }
    };

    const handleOpenModal = (rule = null) => {
        setEditingRule(rule);
        setOpenModal(true);
    };

    // Calculate next available priority
    const getNextPriority = () => {
        if (rules.length === 0) return 100;
        const priorities = rules.map(r => r.priority).sort((a, b) => a - b);
        // Find first gap in priorities, or return max + 10
        for (let i = 0; i < priorities.length - 1; i++) {
            if (priorities[i + 1] - priorities[i] > 1) {
                return priorities[i] + 1;
            }
        }
        return priorities[priorities.length - 1] + 10;
    };

    const handleSaveRule = async (formData) => {
        try {
            // Sanitize form data
            const { id, created_at, updated_at, ...payload } = formData;

            if (editingRule) {
                await LysepAPI.put(`/api/servers/${serverId}/firewall/rules/${editingRule.id}`, payload);
                toast.success('Kural güncellendi');
            } else {
                await LysepAPI.post(`/api/servers/${serverId}/firewall/rules`, { ...payload, config_id: config.id });
                toast.success('Yeni kural eklendi');
            }
            setOpenModal(false);
            fetchData(false);
        } catch (err) {
            toast.error('Kural kaydedilemedi');
        }
    };

    const handleDeleteRule = async (id) => {
        if (!window.confirm('Bu kuralı silmek istediğinize emin misiniz?')) return;
        try {
            await LysepAPI.delete(`/api/servers/${serverId}/firewall/rules/${id}`);
            toast.success('Kural silindi');
            fetchData(false);
        } catch (err) {
            toast.error('Silme işlemi başarısız');
        }
    };



    const handleSync = async () => {
        setSyncing(true);
        const taskId = addTask(`Firewall Sync (${serverId})`);

        try {
            const token = getToken();
            const response = await fetch(`${getBaseURL()}/api/servers/${serverId}/firewall/sync`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include'
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errMsg = `\n[\x1b[31mERROR\x1b[0m] Sunucu hatası: ${errorData.message || response.statusText}`;
                updateTaskLogs(taskId, errMsg);
                finishTask(taskId, false);
                setSyncing(false);
                return;
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let doneStream = false;
            let success = false;

            while (!doneStream) {
                const { value, done } = await reader.read();
                if (done) {
                    doneStream = true;
                    break;
                }
                const chunk = decoder.decode(value, { stream: true });
                updateTaskLogs(taskId, chunk);

                if (chunk.includes('[SUCCESS]') || chunk.includes('[DONE]')) {
                    success = true;
                }
            }

            if (success) {
                toast.success('Başarıyla eşitlendi!');
                fetchData(false);
            }
            finishTask(taskId, success);
        } catch (err) {
            updateTaskLogs(taskId, `\n[\x1b[31mERROR\x1b[0m] Bağlantı hatası: ${err.message}`);
            finishTask(taskId, false);
        } finally {
            setSyncing(false);
        }
    };

    const handleUninstall = async () => {
        if (!window.confirm('DİKKAT: Sunucudaki iptables chain yapısı tamamen kaldırılacak. Bu işlem geri alınamaz. Devam etmek istiyor musunuz?')) {
            return;
        }

        setUninstalling(true);
        const taskId = addTask(`Firewall Uninstall (${serverId})`);

        try {
            const token = getToken();
            const response = await fetch(`${getBaseURL()}/api/servers/${serverId}/firewall/uninstall`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include'
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errMsg = `\n[\x1b[31mERROR\x1b[0m] Sunucu hatası: ${errorData.message || response.statusText}`;
                updateTaskLogs(taskId, errMsg);
                finishTask(taskId, false);
                setUninstalling(false);
                return;
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let doneStream = false;
            let success = false;

            while (!doneStream) {
                const { value, done } = await reader.read();
                if (done) {
                    doneStream = true;
                    break;
                }
                const chunk = decoder.decode(value, { stream: true });
                updateTaskLogs(taskId, chunk);

                if (chunk.includes('[SUCCESS]') || chunk.includes('[DONE]')) {
                    success = true;
                }
            }

            if (success) {
                toast.success('Başarıyla kaldırıldı!');
                fetchData(false);
            }
            finishTask(taskId, success);
        } catch (err) {
            updateTaskLogs(taskId, `\n[\x1b[31mERROR\x1b[0m] Bağlantı hatası: ${err.message}`);
            finishTask(taskId, false);
        } finally {
            setUninstalling(false);
        }
    };

    const isOutOfSync = config && config.rules_file_hash !== currentHash;

    const filteredRules = rules.filter(rule =>
        rule.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rule.ip_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rule.protocol?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rule.source_address?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center p-12 text-gray-400">
                <RefreshCw className="w-8 h-8 animate-spin mb-3 text-blue-500" />
                <p>Yükleniyor...</p>
            </div>
        );
    }

    const ToggleSwitch = ({ checked, onChange, colorClass = 'bg-blue-600' }) => (
        <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="sr-only peer" />
            <div className={`w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${checked ? colorClass : ''}`}></div>
        </label>
    );

    return (
        <div className="space-y-6 animate-fadeIn pb-20">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-lg font-bold text-white flex items-center gap-2">
                        <Shield className="w-5 h-5 text-green-500" />
                        iptables Güvenlik Duvarı
                    </h2>
                    <p className="text-gray-400 text-xs mt-1">Sunucu trafiğini yönetin ve erişim kurallarını yapılandırın.</p>
                </div>
                {isDeployed && (
                    <button
                        disabled={uninstalling}
                        onClick={handleUninstall}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 border border-red-500/20 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <PackageX className={`w-4 h-4 ${uninstalling ? 'animate-pulse' : ''}`} />
                        Güvenlik Duvarını Kaldır
                    </button>
                )}
            </div>

            {/* Deployment Status */}
            {isDeployed && (
                <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                    <div>
                        <h3 className="font-bold text-green-500">Güvenlik Duvarı Kurulmuş</h3>
                        <p className="text-sm text-green-400/80">iptables chain yapısı sunucuda aktif olarak çalışıyor.</p>
                    </div>
                </div>
            )}

            {!isDeployed && (
                <div className="bg-gray-500/10 border border-gray-500/20 rounded-xl p-4 flex items-center gap-3">
                    <XCircle className="w-6 h-6 text-gray-500" />
                    <div>
                        <h3 className="font-bold text-gray-400">Güvenlik Duvarı Kurulmamış</h3>
                        <p className="text-sm text-gray-500">iptables chain yapısı henüz sunucuya kurulmadı. Değişiklikleri uygulayarak kurabilirsiniz.</p>
                    </div>
                </div>
            )}

            {/* Sync Warning */}
            {isOutOfSync && (
                <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <AlertTriangle className="w-6 h-6 text-orange-500" />
                        <div>
                            <h3 className="font-bold text-orange-500">Senkronizasyon Gerekiyor</h3>
                            <p className="text-sm text-orange-400/80">Panelde yapılan değişiklikler henüz sunucuya uygulanmadı.</p>
                        </div>
                    </div>
                    <button
                        onClick={handleSync}
                        disabled={syncing}
                        className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-bold transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                        {syncing ? 'Eşitleniyor...' : 'Değişiklikleri Sunucuya Uygula'}
                    </button>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                {/* Status Card */}
                <div className="md:col-span-4 bg-[#1a1a1a] border border-white/10 rounded-xl p-6 shadow-lg h-full">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            {config && config.status ? <Lock className="w-5 h-5 text-green-500" /> : <Unlock className="w-5 h-5 text-gray-500" />}
                            <h3 className="font-bold text-white">Güvenlik Duvarı</h3>
                        </div>
                        <ToggleSwitch checked={config?.status || false} onChange={handleToggleStatus} colorClass="bg-green-500" />
                    </div>

                    <div className="flex justify-center my-4">
                        <span className={`px-3 py-1 rounded text-xs font-bold border ${config?.status ? 'bg-green-500/10 border-green-500/20 text-green-500' : 'bg-red-500/10 border-red-500/20 text-red-500'}`}>
                            {config?.status ? "SİSTEM KORUNUYOR" : "KORUMA DEVRE DIŞI"}
                        </span>
                    </div>

                    <div className="space-y-4 mt-6">
                        <div className="flex items-center justify-between py-2 border-b border-white/5">
                            <span className="text-sm text-gray-400 font-mono">Seri No (Hash)</span>
                            <span className="text-xs text-white font-mono bg-white/5 px-2 py-1 rounded">{config?.remote_serial || 'v0'}</span>
                        </div>
                        <div className="flex items-center justify-between py-2">
                            <div className="flex items-center gap-2">
                                <Activity className={`w-4 h-4 ${config?.icmp_enabled ? 'text-blue-500' : 'text-gray-500'}`} />
                                <span className="text-sm font-medium text-white">ICMP (Ping)</span>
                            </div>
                            <ToggleSwitch checked={config?.icmp_enabled || false} onChange={(val) => handleUpdatePolicy('icmp_enabled', val)} colorClass="bg-blue-500" />
                        </div>
                        <div className="flex items-center justify-between py-2">
                            <div className="flex items-center gap-2">
                                <Container className={`w-4 h-4 ${config?.docker_protection_enabled ? 'text-blue-500' : 'text-gray-500'}`} />
                                <span className="text-sm font-medium text-white">Docker Bypass Koruması</span>
                            </div>
                            <ToggleSwitch checked={config?.docker_protection_enabled || false} onChange={(val) => handleUpdatePolicy('docker_protection_enabled', val)} colorClass="bg-blue-500" />
                        </div>
                    </div>
                </div>

                {/* Policies Card */}
                <div className="md:col-span-8 bg-[#1a1a1a] border border-white/10 rounded-xl p-6 shadow-lg h-full">
                    <div className="flex items-center gap-2 mb-6">
                        <Shield className="w-5 h-5 text-blue-500" />
                        <h3 className="font-bold text-white">Varsayılan Politikalar</h3>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="bg-[#252525] p-4 rounded-lg border border-white/5">
                            <div className="flex items-center gap-2 mb-3">
                                <LogIn className="w-4 h-4 text-blue-400" />
                                <span className="text-sm font-semibold text-white">Gelen Trafik (Ingress)</span>
                            </div>
                            <select
                                value={config?.default_incoming_policy}
                                onChange={(e) => handleUpdatePolicy('default_incoming_policy', e.target.value)}
                                className="w-full bg-[#1e1e1e] border border-white/10 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                            >
                                <option value="ALLOW">✅ ALLOW (Tümüne İzin Ver)</option>
                                <option value="DENY">🚫 DENY (Tümünü Engelle)</option>
                                <option value="REJECT">✖ REJECT (Tümünü Reddet)</option>
                            </select>
                        </div>

                        <div className="bg-[#252525] p-4 rounded-lg border border-white/5">
                            <div className="flex items-center gap-2 mb-3">
                                <LogOut className="w-4 h-4 text-purple-400" />
                                <span className="text-sm font-semibold text-white">Giden Trafik (Egress)</span>
                            </div>
                            <select
                                value={config?.default_outgoing_policy}
                                onChange={(e) => handleUpdatePolicy('default_outgoing_policy', e.target.value)}
                                className="w-full bg-[#1e1e1e] border border-white/10 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                            >
                                <option value="ALLOW">✅ ALLOW (Tümüne İzin Ver)</option>
                                <option value="DENY">🚫 DENY (Tümünü Engelle)</option>
                                <option value="REJECT">✖ REJECT (Tümünü Reddet)</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-white/10">
                <div className="flex gap-4">
                    <button
                        onClick={() => setTabValue(0)}
                        className={`flex items-center gap-2 pb-2 px-2 text-sm font-medium transition-colors border-b-2 ${tabValue === 0 ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-400 hover:text-white'}`}
                    >
                        <List className="w-4 h-4" />
                        Kuralları Yönet
                    </button>
                    <button
                        onClick={() => setTabValue(1)}
                        className={`flex items-center gap-2 pb-2 px-2 text-sm font-medium transition-colors border-b-2 ${tabValue === 1 ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-400 hover:text-white'}`}
                    >
                        <FileText className="w-4 h-4" />
                        Kural Dosyası Önizleme
                    </button>
                </div>
            </div>

            {/* Tab Content */}
            {tabValue === 0 ? (
                <>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-6 mb-4">
                        <div>
                            <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                <Shield className="w-5 h-5 text-gray-400" />
                                Güvenlik Kuralları
                            </h3>
                        </div>
                        <div className="flex gap-3 flex-1 max-w-xl">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                <input
                                    type="text"
                                    placeholder="Kurallarda ara..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full bg-[#1e1e1e] border border-white/10 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                                />
                            </div>
                            <button
                                onClick={() => handleOpenModal()}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold transition-colors whitespace-nowrap"
                            >
                                <Plus className="w-4 h-4" />
                                Kural Ekle
                            </button>
                        </div>
                    </div>

                    <div className="bg-[#1a1a1a] border border-white/10 rounded-xl overflow-hidden shadow-lg">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-20">Öncelik</TableHead>
                                    <TableHead className="w-16">Durum</TableHead>
                                    <TableHead className="w-32">Yön / İşlem</TableHead>
                                    <TableHead className="w-24">Protokol</TableHead>
                                    <TableHead>Kaynak</TableHead>
                                    <TableHead>Hedef Port</TableHead>
                                    <TableHead>Açıklama</TableHead>
                                    <TableHead className="text-right w-24">İşlemler</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredRules.map((rule) => (
                                    <TableRow key={rule.id} className={`${!rule.is_enabled ? 'opacity-50' : ''}`}>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <span className="font-bold text-blue-400">{rule.priority}</span>
                                                {rule.is_system_rule && (
                                                    <Activity className="w-3 h-3 text-cyan-500" title="Sistem Kuralı" />
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {rule.is_enabled ? (
                                                <Badge variant="success" className="gap-1 px-2">
                                                    <CheckCircle2 className="w-3 h-3" />
                                                    Aktif
                                                </Badge>
                                            ) : (
                                                <Badge variant="secondary" className="gap-1 px-2 text-gray-500">
                                                    <XCircle className="w-3 h-3" />
                                                    Pasif
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col gap-1 items-start">
                                                <Badge variant={rule.direction === 'INGRESS' ? 'default' : 'purple'} className="gap-1">
                                                    {rule.direction === 'INGRESS' ? <LogIn className="w-3 h-3" /> : <LogOut className="w-3 h-3" />}
                                                    {rule.direction}
                                                </Badge>
                                                <Badge variant={rule.action === 'ALLOW' ? 'success' : 'destructive'} className="text-[10px] px-1.5 py-0">
                                                    {rule.action}
                                                </Badge>
                                            </div>
                                        </TableCell>
                                        <TableCell className="font-mono text-gray-300">{rule.protocol}</TableCell>
                                        <TableCell className="font-mono">
                                            <Badge variant="secondary" className="font-mono text-white">
                                                {rule.source_address}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            {rule.destination_ports ? (
                                                <Badge variant="outline" className="font-mono text-gray-300 border-white/10">
                                                    {rule.destination_ports}
                                                </Badge>
                                            ) : (
                                                <span className="text-xs text-gray-500 italic">TÜMÜ</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-gray-400 italic">{rule.description || '-'}</TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <IconButton
                                                    onClick={() => handleOpenModal(rule)}
                                                    icon={Edit2}
                                                    variant="info"
                                                    title="Düzenle"
                                                />
                                                <IconButton
                                                    onClick={() => handleDeleteRule(rule.id)}
                                                    disabled={rule.is_system_rule}
                                                    icon={Trash2}
                                                    variant="destructive"
                                                    className="hover:bg-red-500/10 hover:text-red-400"
                                                    title="Sil"
                                                />
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredRules.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center text-gray-500 italic py-12">
                                            Kural bulunamadı.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </>
            ) : (
                <div className="bg-[#1a1a1a] border border-white/10 rounded-xl p-4 shadow-lg min-h-[500px] mt-6 relative">
                    <div className="absolute top-4 right-4 z-10">
                        <button
                            onClick={handleCopyPreview}
                            className="bg-[#252525] hover:bg-[#333] border border-white/10 text-white px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-2 transition-colors"
                        >
                            <Copy className="w-3.5 h-3.5" />
                            Kopyala
                        </button>
                    </div>
                    {previewLoading ? (
                        <div className="flex flex-col items-center justify-center h-full text-gray-500 gap-2 mt-20">
                            <RefreshCw className="w-8 h-8 animate-spin" />
                            <p>Önizleme oluşturuluyor...</p>
                        </div>
                    ) : (
                        <pre className="font-mono text-xs sm:text-sm text-gray-300 overflow-auto h-[600px] p-4 bg-[#111] rounded-lg border border-white/5 leading-relaxed">
                            {rulesPreview || '# Önizleme yok'}
                        </pre>
                    )}
                </div>
            )}

            {/* Modals & Overlays */}
            <FirewallRuleModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                onSave={handleSaveRule}
                initialData={editingRule}
                existingRules={rules}
                suggestedPriority={editingRule ? editingRule.priority : getNextPriority()}
            />
        </div>
    );
};

export default FirewallTab;
